import React, { useEffect, useState } from 'react'
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom'
import { me, logout } from '../lib/api'
import Nav from '../sections/Nav'
import Footer from '../sections/Footer'
import { RoutesEnum } from '../lib/routes'
import { useLang } from '../lib/useLang'
import './Layout.css'

function LangSwitch({ lang, setLang }) {
  return (
    <div className='lang-switch'>
      {['en', 'ru'].map((l) => (
        <button
          key={l}
          type='button'
          onClick={() => setLang(l)}
          className={`lang-switch-btn ${lang === l ? 'is-active' : ''}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  )
}

const PUBLIC_ROUTES = [RoutesEnum.ROOT, RoutesEnum.AUTH]

function getCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return m ? decodeURIComponent(m[1]) : ''
}

export default function Layout() {
  const cachedEmail = getCookie('mod_user')
  const [email, setEmail] = useState(cachedEmail)
  const [loading, setLoading] = useState(!cachedEmail)
  const navigate = useNavigate()
  const loc = useLocation()
  const { lang, setLang } = useLang()

  const isPublicRoute = PUBLIC_ROUTES.includes(loc.pathname)
  const isLanding = loc.pathname === RoutesEnum.ROOT
  const isAuth = loc.pathname === RoutesEnum.AUTH
  const isPublicSubPage = [
    RoutesEnum.PRIVACY,
    RoutesEnum.TERMS,
    RoutesEnum.CONSENT,
    RoutesEnum.COOKIES,
    RoutesEnum.PLANS,
    RoutesEnum.CONTACT,
    RoutesEnum.USE_CASES,
  ].includes(loc.pathname)
  const isPublicLike = isLanding || isPublicSubPage

  useEffect(() => {
    if (isPublicRoute || isPublicSubPage) {
      setLoading(false)
      me()
        .then((d) => {
          setEmail(d.email || '')
          if (d.email) {
            document.cookie = `mod_user=${encodeURIComponent(d.email)}; Path=/; SameSite=Lax; Max-Age=2592000`
          }
        })
        .catch(() => {
          document.cookie = 'mod_user=; Path=/; Max-Age=0'
          setEmail('')
        })
      return
    }

    let mounted = true
    me()
      .then((d) => {
        if (!mounted) return
        setEmail(d.email || '')
        if (d.email) {
          document.cookie = `mod_user=${encodeURIComponent(d.email)}; Path=/; SameSite=Lax; Max-Age=2592000`
        }
        setLoading(false)
      })
      .catch(() => {
        if (!mounted) return
        document.cookie = 'mod_user=; Path=/; Max-Age=0'
        setEmail('')
        setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [loc.pathname, isPublicRoute, isPublicSubPage])

  const onLogout = async () => {
    try { await logout() } catch {}
    document.cookie = 'mod_user=; Path=/; Max-Age=0'
    setEmail('')
    navigate(RoutesEnum.ROOT, { replace: true })
  }

  if (loading) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-cream'>
        <span className='loading loading-spinner loading-lg' />
      </div>
    )
  }

  /* ---------------- Public pages: landing + sub-pages (Plans / Privacy / Contact / Use Cases) ----- */
  if (isPublicLike) {
    return (
      <div className={`min-h-screen bg-cream text-ink lang-${lang}`}>
        <Nav userEmail={email} onLogout={onLogout} />
        <main>
          <Outlet />
        </main>
        <Footer />
      </div>
    )
  }

  /* ---------------- Auth: minimal layout with close button ---------------- */
  if (isAuth) {
    return (
      <div className={`min-h-screen bg-cream text-ink lang-${lang}`}>
        <div className='absolute top-0 left-0 right-0 z-50'>
          <div className='mx-auto max-w-7xl px-6 lg:px-10 h-[64px] flex items-center justify-between'>
            <Link to={RoutesEnum.ROOT} className='brand text-base'>
              <span className='brand-mark'>
                <span className='brand-letter persistent'>M</span>
                <span className='brand-letter'>O</span>
                <span className='brand-letter'>D</span>
                <span className='brand-letter'>E</span>
                <span className='brand-letter'>L</span>
                <span className='brand-letter'>L</span>
                <span className='brand-letter'>E</span>
                <span className='brand-letter persistent'>R</span>
              </span>
            </Link>
            <div className='flex items-center gap-3'>
              <LangSwitch lang={lang} setLang={setLang} />
              <button
                type='button'
                onClick={() => navigate(RoutesEnum.ROOT)}
                className='nav-link'
                aria-label='Close'
                style={{ padding: 8 }}
              >
                <svg width='20' height='20' viewBox='0 0 20 20' fill='none' stroke='currentColor' strokeWidth='1.5'>
                  <path d='M5 5l10 10M15 5l-10 10' strokeLinecap='round' />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <main className='flex-1'>
          <Outlet />
        </main>
      </div>
    )
  }

  /* ---------------- Platform (signed-in) pages ---------------- */
  return (
    <div className={`app-shell flex min-h-screen flex-col lang-${lang}`}>
      <Nav userEmail={email} onLogout={onLogout} />
      <main className='flex-1'>
        <div key={loc.pathname} className='animate-fade-in h-full' style={{ animationDuration: '0.4s' }}>
          <Outlet />
        </div>
      </main>
    </div>
  )
}
